import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeCheck, Bike, CalendarDays, Droplets, Dumbbell, Flame, Hand, HeartPulse, LayoutGrid, LifeBuoy, Lock, Mountain,
  Music, PartyPopper, ScanLine, ShieldCheck, Sparkles, Tent, Thermometer, Timer, TrendingUp, Trophy, UserRound, Users,
  Waves, Wind,
} from 'lucide-react';
import PhotoSlot from './PhotoSlots';
import { PHOTO_ASSETS } from '../data/photoAssets';
import { MEDIA_SLOTS, mediaDataProps } from '../lib/mediaTruth';
import { publicHref } from '../lib/publicUrls';
import './ProgramsJourney.css';

// The Programs page as a scroll journey through the club (owner direction, Sept 30; the MX4 coach
// profiles are the blueprint). Each stop's photo and haze fade into the next while its panel rises
// in with bullets, a line illustration and the next step. Every fact comes from the program's own page.
// The Amenities page tells the same kind of journey through the club's spaces (ClubJourney).

// Line illustrations (decorative), drawn on as each panel arrives.
const LINES = {
  pulse: 'M0 12h58l6-9 8 18 7-14 5 5h40l6-9 8 18 7-14 5 5h82',
  steps: 'M0 21h44v-5h44v-5h44v-5h44v-5h64',
  barbell: 'M0 12h240M44 3v18M52 0v24M188 0v24M196 3v18',
  cadence: 'M0 12q15-11 30 0t30 0 30 0 30 0 30 0 30 0 30 0 30 0',
  breath: 'M0 12C40-2 80 26 120 12S200-2 240 12',
  spring: 'M0 12h64c4-12 12-12 12 0s8 12 12 0 8-12 12 0 8 12 12 0 8-12 12 0 8 12 12 0 8-12 12 0h68',
  waves: 'M0 8q20-8 40 0t40 0 40 0 40 0 40 0 40 0M0 18q20-8 40 0t40 0 40 0 40 0 40 0 40 0',
  bounce: 'M0 22q22-26 44 0 18-20 36 0 14-14 28 0 10-9 20 0 7-6 14 0h98',
  ripple: 'M0 12h84c8 0 10-7 18-7s10 14 18 14 10-14 18-14 10 7 18 7h84',
  court: 'M0 22h240M40 22q80-36 160 0M120 22V6',
  climb: 'M0 22h40l20-8 14 4 18-12 16 6 20-10 18 4 94 0',
  steam: 'M60 22c-8-6 8-10 0-16s8-8 0-6M120 22c-8-6 8-10 0-16s8-8 0-6M180 22c-8-6 8-10 0-16s8-8 0-6',
  stride: 'M0 18h40l10-12 10 12h40l10-12 10 12h40l10-12 10 12h50',
};

export const JOURNEY = [
  {
    slug: 'mx4', name: 'MX4', rgb: '196 52 72', line: 'pulse', media: MEDIA_SLOTS.programsMx4,
    title: ['Burn fat.', 'Build muscle.'],
    lede: 'Small-group training that targets cardio, power, strength, and endurance.',
    stat: ['45', 'minute small-group sessions'],
    points: [
      [Timer, 'Circuits with 1-minute intervals'],
      [LayoutGrid, 'Stations for upper, lower, and full-body work'],
      [TrendingUp, 'Progression at your level, for all fitness levels'],
      [CalendarDays, 'An hour-long Weekend Warrior Workout on Saturdays'],
    ],
    path: '/programs/mx4', cta: 'Explore MX4', next: ['/programs/mx4#mx4-inquiry', 'Request MX4 Information'],
  },
  {
    slug: 'personal-training', name: 'Personal Training', rgb: '118 196 138', line: 'steps', media: MEDIA_SLOTS.programsPersonalTraining,
    title: ['Make your goals', 'a reality.'],
    lede: 'One-on-one customized fitness programs designed for your goals.',
    stat: ['1:1', 'coaching built around you'],
    points: [
      [UserRound, 'Personable trainers with personalized plans'],
      [Dumbbell, 'Strength training, weight loss, and mobility'],
      [HeartPulse, 'Injury recovery, plus senior and youth fitness'],
    ],
    extra: {
      icon: ScanLine, eyebrow: 'Supporting Personal Training service', title: 'Body Scans',
      text: 'Set goals and track the changes in your body shape and dimensions as you train. A free 3D body scan comes with your package.',
    },
    path: '/programs/personal-training', cta: 'Explore Personal Training', next: ['/programs/personal-training#pt-inquiry', 'Pick My Trainer'],
  },
  {
    slug: 'open-floor', name: 'Open Floor', label: 'Weight Training', rgb: '214 124 62', line: 'barbell', media: MEDIA_SLOTS.amenitiesFacility,
    title: ['The #1 place', 'to work out in Brentwood.'],
    lede: 'Weight rooms, cardio, and group exercise spaces, with coached weight training classes included in your membership.',
    points: [
      [Dumbbell, 'Weight rooms with racks and free weights'],
      [Users, 'Coached classes like Body Blast, F.I.T., and Muscle Pump'],
      [Mountain, 'A two-story Rockwall, open once you earn the certificate'],
      [BadgeCheck, 'Weight training classes included with membership'],
    ],
    path: '/schedule/weight-training', cta: 'Explore Weight Training', next: ['/amenities', 'See the Amenities'],
  },
  {
    slug: 'cardio', name: 'Cardio', label: 'Group Classes', rgb: '72 124 220', line: 'cadence', media: MEDIA_SLOTS.programsCardio,
    title: ['Heart-pounding', 'cardio classes.'],
    lede: 'From spin sessions to empowering kickboxing, a dynamic fusion of cardio under one membership.',
    points: [
      [Bike, 'Spin rides like The ZONE and Battle in the Saddle'],
      [Music, 'Zumba, set to upbeat percussion'],
      [Flame, 'Kickboxing for a full-body burn and stress relief'],
      [Sparkles, 'Fusions like Cardio at the Barre and Pedal and Pump'],
    ],
    path: '/schedule/cardio', cta: 'Explore Cardio', next: ['/schedule/cardio#cardio-schedule', 'See Cardio Classes'],
  },
  {
    slug: 'mind-body', name: 'Mind & Body', label: 'Group Classes', rgb: '158 128 220', line: 'breath', media: MEDIA_SLOTS.programsMindBody,
    title: ['Elevate', 'your essence.'],
    lede: 'Build strength, mobility, balance, and calm through yoga, barre, Mat Pilates, and Power Stretch.',
    points: [
      [Wind, 'Yoga, Yoga Flow, and Yoga Muscle Fusion'],
      [Sparkles, 'Barre and Barre-Lates for posture and tone'],
      [TrendingUp, 'Mat Pilates and Power Stretch for mobility'],
      [Waves, 'Water Aerobics, easy on the joints'],
    ],
    path: '/schedule/mind-body', cta: 'Explore Mind & Body', next: ['/schedule/mind-body#mind-body-schedule', 'See Mind & Body Classes'],
  },
  {
    slug: 'reformer-pilates', name: 'Reformer Pilates', rgb: '140 100 220', line: 'spring', media: MEDIA_SLOTS.programsReformerPilates,
    title: ['Sculpt, tone,', 'and strengthen.'],
    lede: 'A supportive, adaptable workout for strength, mobility, and precise movement, in Brentwood’s top Reformer Pilates studio.',
    stat: ['30', 'minute demo, free'],
    points: [
      [Sparkles, 'Full-body toning for a leaner physique'],
      [ShieldCheck, 'Core strength for posture and balance'],
      [Wind, 'Flexibility and range of motion'],
      [HeartPulse, 'Low-impact and joint friendly'],
    ],
    path: '/programs/reformer-pilates', cta: 'Explore Reformer Pilates', next: ['/programs/reformer-pilates#reformer-demo', 'Request a Free Demo'],
  },
  {
    slug: 'swimming', name: 'Swimming Lessons', label: 'Aquatics', rgb: '40 172 192', line: 'waves', media: MEDIA_SLOTS.programsSwimming,
    title: ['Two indoor pools,', 'year-round.'],
    lede: 'Heated, indoor, and open all year, for swim lessons, laps, and water fitness.',
    stat: ['93°F', 'warm-water teaching pool'],
    points: [
      [Thermometer, 'Lessons in our warm-water teaching pool'],
      [Waves, 'Lap lanes for members during club hours'],
      [Users, 'Water Aerobics and Advanced Swim Conditioning'],
      [LifeBuoy, 'Water Safety Camps before summer adventures'],
    ],
    path: '/programs/swim', cta: 'Explore Swimming Lessons', next: ['/programs/swim#swim-inquiry', 'Ask About Swim Lessons'],
  },
  {
    slug: 'kids-family', name: 'Kids & Family', rgb: '218 156 44', line: 'bounce', media: MEDIA_SLOTS.programsKidsFamily,
    title: ['Families', 'thrive here.'],
    lede: 'Pool parties, birthday parties, kids camps, childcare, and more, in a youth space cleaned around the clock.',
    stat: ['2-story', 'indoor jungle gym'],
    points: [
      [Sparkles, 'A movie room, air hockey, and a mini basketball court'],
      [ShieldCheck, 'Background-checked, CPR-certified staff in a fully gated Kid’s Club'],
      [PartyPopper, 'Pool parties, birthday parties, and Parent’s Night Out'],
      [Tent, 'Summer camps with water games, rock climbing, and crafts'],
    ],
    path: '/programs/kids-and-family', cta: 'Explore Kids & Family', next: ['/childcare/birthday-party', 'Plan a Birthday Party'],
  },
  {
    slug: 'massage', name: 'Massage Therapy', label: 'Recovery', rgb: '128 176 152', line: 'ripple', media: MEDIA_SLOTS.programsMassage, logoUntilPhoto: true,
    title: ['Relieve tension.', 'Support well-being.'],
    lede: 'Personalized therapeutic massage and HydroMassage to help relieve tension and support total well-being.',
    points: [
      [Hand, 'Sports recovery and deep tissue massage'],
      [HeartPulse, 'Pre-natal and needs-specific massage'],
      [Droplets, 'HydroMassage with targeted water jets'],
      [Thermometer, 'Sauna and steam rooms in the locker rooms'],
    ],
    path: '/programs/massage', cta: 'Explore Massage Therapy', next: ['/programs/massage#massage-inquiry', 'Ask About Massage'],
  },
  {
    slug: 'courts', name: 'Courts & Club Life', rgb: '112 146 196', line: 'court', media: MEDIA_SLOTS.amenitiesBasketball,
    title: ['More than', 'a traditional gym.'],
    lede: 'Spaces for every fitness level and interest, and a community that comes together.',
    stat: ['6', 'hoops on a full-size indoor court'],
    points: [
      [Trophy, 'Basketball, plus indoor pickleball for a range of skill levels'],
      [Lock, 'Private locker rooms with warm showers and digital lockers'],
      [CalendarDays, 'Community favorites like the free Trunk or Treat in October'],
    ],
    path: '/amenities', cta: 'Explore the Amenities', next: ['/book-a-tour', 'Book a Tour'],
  },
];

// The backdrop covers the screen, so on an upright phone a landscape photo shows at about 1.5 screen-heights wide.
const BACKDROP_SIZES = '(orientation: portrait) 150vh, 100vw';
const pad = (index) => String(index + 1).padStart(2, '0');
const hasPhoto = (stop) => Boolean(PHOTO_ASSETS[stop.media.id]);
const motionAllowed = () => typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function'
  && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const JourneyLine = ({ name }) => (
  <svg className="dv-pj__line" viewBox="0 0 240 24" preserveAspectRatio="none" aria-hidden="true" focusable="false">
    <path pathLength="1" d={LINES[name]} />
  </svg>
);

function Scene({ stop, active }) {
  return (
    <div className={`dv-pj__scene${active ? ' is-active' : ''}`} style={{ '--pj-rgb': stop.rgb }}>
      {stop.logoUntilPhoto && !hasPhoto(stop) ? (
        <span className="dv-pj__photo dv-pj__photo--logo"><img src="/Delta-Valley-Logo.png" alt="" /></span>
      ) : (
        <PhotoSlot {...mediaDataProps(stop.media)} id={stop.media.id} label={`${stop.media.label} photo`} sizes={BACKDROP_SIZES} className="dv-pj__photo" />
      )}
      <span className="dv-pj__haze" />
    </div>
  );
}

// On-page jumps (like the tour request) stay plain links; other pages go through the router.
const StepLink = ({ href, className, children }) => (href.startsWith('#')
  ? <a className={className} href={href}>{children}</a>
  : <Link className={className} to={publicHref(href)}>{children}</Link>);

function Stop({ stop, index, total, prefix }) {
  const { slug, name, label, line, title, lede, stat, points, extra, path, cta, next } = stop;
  const ExtraIcon = extra?.icon;
  return (
    <li id={`${prefix}-${slug}`} className="dv-pj__stop" data-stop={index} style={{ '--pj-rgb': stop.rgb }}>
      <article className="dv-pj__card" aria-labelledby={`${prefix}-${slug}-title`}>
        <p className="dv-eyebrow dv-pj__eyebrow">
          <span className="dv-pj__count">{pad(index)} / {pad(total - 1)}</span>
          <span>{name}{label ? ` · ${label}` : ''}</span>
        </p>
        <JourneyLine name={line} />
        <h3 className="dv-size-heading dv-pj__title" id={`${prefix}-${slug}-title`}>
          <span>{title[0]}</span>
          <em>{title[1]}</em>
        </h3>
        <p className="dv-size-lede dv-pj__lede">{lede}</p>
        {stat && (
          <p className="dv-pj__stat">
            <strong className="dv-size-heading">{stat[0]}</strong>
            <span>{stat[1]}</span>
          </p>
        )}
        <ul className="dv-pj__points">
          {points.map(([Icon, text], pointIndex) => (
            <li key={text} className="dv-size-body" style={{ '--pj-i': pointIndex }}>
              <span className="dv-pj__icon"><Icon size={18} strokeWidth={1.6} aria-hidden="true" /></span>
              {text}
            </li>
          ))}
        </ul>
        {extra && (
          <aside className="dv-pj__extra" aria-labelledby="body-scans-title">
            <ExtraIcon size={20} strokeWidth={1.6} aria-hidden="true" />
            <div>
              <p className="dv-eyebrow">{extra.eyebrow}</p>
              <h4 className="dv-size-subheading" id="body-scans-title">{extra.title}</h4>
              <p className="dv-size-body">{extra.text}</p>
            </div>
          </aside>
        )}
        <div className="dv-service-actions dv-pj__actions">
          <StepLink className="dv-button dv-button--light" href={path}>{cta}</StepLink>
          <StepLink className="dv-button dv-button--outline-light" href={next[0]}>{next[1]}</StepLink>
        </div>
      </article>
    </li>
  );
}

const PROGRAMS_INTRO = {
  eyebrow: 'The Delta Valley journey',
  title: ['Every part of the club,', 'one scroll away.'],
  lede: 'Take the tour from MX4 to the courts. Every stop shows what it is, who it’s for, and your next step.',
  quote: (
    <blockquote className="dv-pj__quote">
      <p className="dv-size-lede">
        “Delta Valley has a program for every experience level and goal! They constantly
        upgrade the facilities and the trainers and coaches are friendly, enthusiastic,
        and professional.”
      </p>
      <footer>Delta Valley member</footer>
    </blockquote>
  ),
};

export default function ProgramsJourney({ stops = JOURNEY }) {
  return <ClubJourney id="all-programs" prefix="program" stops={stops} intro={PROGRAMS_INTRO} indexLabel="Jump to a program" />;
}

export function ClubJourney({ id, prefix, stops, intro, indexLabel, railLabel = 'Club journey' }) {
  const rootRef = useRef(null);
  const [active, setActive] = useState(0);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window.IntersectionObserver !== 'function') return undefined;
    const items = [...root.querySelectorAll('.dv-pj__stop')];
    // The stop crossing the middle of the screen owns the backdrop and the progress rail.
    const current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) setActive(Number(entry.target.dataset.stop)); });
    }, { rootMargin: '-50% 0px -50% 0px' });
    items.forEach((item) => current.observe(item));
    if (!motionAllowed()) return () => current.disconnect();
    // Panels rise in once, as they arrive.
    setAnimated(true);
    const reveal = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        reveal.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -18% 0px', threshold: 0.12 });
    items.forEach((item) => reveal.observe(item));
    return () => { current.disconnect(); reveal.disconnect(); };
  }, [stops]);

  const stop = stops[active] || stops[0];
  return (
    <section id={id} ref={rootRef} className={`dv-pj${animated ? ' dv-pj--animated' : ''}`} aria-labelledby={`${id}-title`} style={{ '--pj-rgb': stop.rgb }}>
      <header className="dv-pj__intro">
        <div className="dv-pj__intro-copy">
          <p className="dv-eyebrow">{intro.eyebrow}</p>
          <h2 className="dv-size-heading dv-pj__intro-title" id={`${id}-title`}>
            <span>{intro.title[0]}</span>
            <em>{intro.title[1]}</em>
          </h2>
          <p className="dv-size-lede">{intro.lede}</p>
        </div>
        {intro.quote}
        <nav className="dv-pj__index" aria-label={indexLabel}>
          <ol>
            {stops.map((item, index) => (
              <li key={item.slug} style={{ '--pj-rgb': item.rgb }}>
                <a href={`#${prefix}-${item.slug}`}><span aria-hidden="true">{pad(index)}</span>{item.name}</a>
              </li>
            ))}
          </ol>
        </nav>
      </header>

      <div className="dv-pj__stage">
        <div className="dv-pj__backdrop" aria-hidden="true">
          {stops.map((item, index) => <Scene key={item.slug} stop={item} active={index === active} />)}
        </div>

        <div className="dv-pj__progress" aria-hidden="true">
          <span className="dv-pj__progress-label">{pad(active)} · {stop.name}</span>
          <span className="dv-pj__progress-track"><span style={{ width: `${((active + 1) / stops.length) * 100}%` }} /></span>
        </div>

        <nav className="dv-pj__rail" aria-label={railLabel}>
          <ol>
            {stops.map((item, index) => (
              <li key={item.slug} className={index === active ? 'is-active' : undefined} style={{ '--pj-rgb': item.rgb }}>
                <a href={`#${prefix}-${item.slug}`} aria-current={index === active ? 'step' : undefined}>{item.name}</a>
              </li>
            ))}
          </ol>
        </nav>

        <ol className="dv-pj__stops">
          {stops.map((item, index) => <Stop key={item.slug} stop={item} index={index} total={stops.length} prefix={prefix} />)}
        </ol>
      </div>
    </section>
  );
}
